import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  FormFeedback,
  Spinner,
} from "reactstrap";

const PROFILE_TAB = "profile";
const PASSWORD_TAB = "password";

const Profile = () => {
  const [activeTab, setActiveTab] = useState(PROFILE_TAB);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileApiLoading, setProfileApiLoading] = useState(true);
  const [profileAlert, setProfileAlert] = useState(null);
  const [passwordAlert, setPasswordAlert] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [passwordFormErrors, setPasswordFormErrors] = useState({});
  const [educationOptions, setEducationOptions] = useState({
    cities: [],
    qualifications: [],
  });
  const [profileData, setProfileData] = useState({
    roll_number: "",
    name: "",
    mobile: "",
    email: "",
    cnic: "",
    qualification: "",
    whatsapp: "",
    city: "",
    address: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${global.config.Token}`;
    populateFromLocalStorage();
    loadEducationOptions();
    loadProfileFromApi();
  }, []);

  const getApiBase = () => global.config.URL.api.lms.replace(/lms\/$/, "");

  const getOptionId = item => item?.id ?? item?.value ?? "";
  const getOptionLabel = item =>
    item?.name ??
    item?.title ??
    item?.city ??
    item?.qualification ??
    item?.label ??
    "Unknown";

  const normalizeSelectedOptionValue = (value, options) => {
    if (value === null || value === undefined || value === "") return "";

    const normalizedValue = String(value).trim();
    const hasIdMatch = options.find(item => String(getOptionId(item)) === normalizedValue);
    if (hasIdMatch) return normalizedValue;

    const byLabel = options.find(
      item => String(getOptionLabel(item)).trim().toLowerCase() === normalizedValue.toLowerCase()
    );
    return byLabel ? String(getOptionId(byLabel)) : normalizedValue;
  };

  const loadEducationOptions = async () => {
    const apiBase = getApiBase();
    try {
      const res = await axios.get(`${apiBase}student/education-cities`);
      if (res?.data?.success) {
        setEducationOptions({
          cities: res.data.cities || [],
          qualifications: res.data.qualifications || [],
        });
      }
    } catch (_error) {
      try {
        const fallback = await axios.get(`${apiBase}public/education-cities`);
        if (fallback?.data?.success) {
          setEducationOptions({
            cities: fallback.data.cities || [],
            qualifications: fallback.data.qualifications || [],
          });
        }
      } catch (_fallbackError) {
        setEducationOptions({
          cities: [],
          qualifications: [],
        });
      }
    }
  };

  const populateFromLocalStorage = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    if (!authUser || !authUser.data) return;

    const data = authUser.data;
    setProfileData(prev => ({
      ...prev,
      roll_number: data.roll_no || data.roll_number || "",
      name: data.name || "",
      mobile: data.mobile || "",
      email: data.email || "",
      cnic: data.cnic || "",
      qualification: data.qualification || "",
      whatsapp: data.whatsapp || data.whatsapp_number || "",
      city: data.city || "",
      address: data.address || "",
    }));
  };

  const loadProfileFromApi = async () => {
    setProfileApiLoading(true);
    try {
      const res = await axios.get(global.config.URL.api.lms + "my-profile");
      if (res?.data?.success && res.data.data) {
        const data = res.data.data;
        setProfileData(prev => ({
          ...prev,
          roll_number: data.roll_no || data.roll_number || prev.roll_number,
          name: data.name || prev.name,
          mobile: data.mobile || prev.mobile,
          email: data.email || prev.email,
          cnic: data.cnic || prev.cnic,
          qualification: data.qualification_id || data.qualification || prev.qualification,
          whatsapp: data.whatsapp || data.whatsapp_number || prev.whatsapp,
          city: data.city_id || data.city || prev.city,
          address: data.address || prev.address,
        }));
      }
    } catch (_error) {
      // Keep localStorage-backed values when API endpoint is unavailable.
    } finally {
      setProfileApiLoading(false);
    }
  };

  useEffect(() => {
    if (educationOptions.cities.length || educationOptions.qualifications.length) {
      setProfileData(prev => {
        const normalizedCity = normalizeSelectedOptionValue(prev.city, educationOptions.cities);
        const normalizedQualification = normalizeSelectedOptionValue(
          prev.qualification,
          educationOptions.qualifications
        );

        if (normalizedCity === prev.city && normalizedQualification === prev.qualification) {
          return prev;
        }

        return {
          ...prev,
          city: normalizedCity,
          qualification: normalizedQualification,
        };
      });
    }
  }, [educationOptions]);

  const handleProfileChange = event => {
    const { name, value } = event.target;
    setFormErrors(prev => ({ ...prev, [name]: null }));
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = event => {
    const { name, value } = event.target;
    setPasswordFormErrors(prev => ({ ...prev, [name]: null }));
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async event => {
    event.preventDefault();
    setProfileLoading(true);
    setProfileAlert(null);
    setFormErrors({});

    try {
      const payload = {
        name: profileData.name,
        mobile: profileData.mobile,
        qualification: profileData.qualification ? Number(profileData.qualification) : null,
        whatsapp: profileData.whatsapp,
        city: profileData.city ? Number(profileData.city) : null,
        address: profileData.address,
      };
      const res = await axios.post(global.config.URL.api.lms + "update-profile", payload);
      if (res?.data?.success) {
        setProfileAlert({ type: "success", text: "Profile updated successfully." });
      } else {
        setProfileAlert({ type: "danger", text: "Profile update failed. Please try again." });
      }
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === "object") {
        setFormErrors(backendErrors);
      }
      setProfileAlert({
        type: "danger",
        text: error?.response?.data?.message || "Profile update failed. Please try again.",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async event => {
    event.preventDefault();
    setPasswordLoading(true);
    setPasswordAlert(null);
    setPasswordFormErrors({});

    if (passwordData.password !== passwordData.password_confirmation) {
      setPasswordFormErrors({
        password_confirmation: ["Confirm password does not match."],
      });
      setPasswordAlert({ type: "danger", text: "Confirm password does not match." });
      setPasswordLoading(false);
      return;
    }

    try {
      const res = await axios.post(global.config.URL.api.lms + "update-password", passwordData);
      if (res?.data?.success) {
        setPasswordAlert({ type: "success", text: "Password updated successfully." });
        setPasswordData({
          current_password: "",
          password: "",
          password_confirmation: "",
        });
      } else {
        setPasswordAlert({
          type: "danger",
          text: res?.data?.message || "Password update failed. Please try again.",
        });
      }
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === "object") {
        setPasswordFormErrors(backendErrors);
      }
      setPasswordAlert({
        type: "danger",
        text: error?.response?.data?.message || "Password update failed. Please try again.",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleTabSwitch = (event, tab) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveTab(tab);
  };

  const sideMenuButtonClasses = tab =>
    `list-group-item list-group-item-action border-0 rounded-2 profile-tab-btn ${
      activeTab === tab ? "active profile-tab-btn-active text-white" : "text-dark"
    }`;

  return (
    <React.Fragment>
      <div className="page-content">
        <Row className="g-3">
          <Col lg={3} md={4} sm={12} className="profile-left-column">
            <Card>
              <CardBody className="p-0">
                <div className="list-group list-group-flush profile-tab-list p-2">
                  <button
                    type="button"
                    className={sideMenuButtonClasses(PROFILE_TAB)}
                    onClick={event => handleTabSwitch(event, PROFILE_TAB)}
                  >
                    <i className="mdi mdi-account-outline me-2"></i>
                    My Profile
                  </button>
                  <button
                    type="button"
                    className={sideMenuButtonClasses(PASSWORD_TAB)}
                    onClick={event => handleTabSwitch(event, PASSWORD_TAB)}
                  >
                    <i className="mdi mdi-lock-reset me-2"></i>
                    Password Reset
                  </button>
                  <Link to="/logout" className="list-group-item list-group-item-action border-0 rounded-2 mt-2 text-danger">
                    <i className="mdi mdi-logout me-2"></i>
                    Logout
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={9} md={8} sm={12} className="profile-content-column">
            {activeTab === PROFILE_TAB && (
              <Card>
                <CardBody>
                  <CardTitle className="h4 mb-4 text-uppercase text-center">My Profile</CardTitle>
                  {profileApiLoading ? (
                    <div className="d-flex align-items-center justify-content-center py-5">
                      <Spinner color="success" />
                    </div>
                  ) : (
                    <>
                  {profileAlert && <Alert color={profileAlert.type}>{profileAlert.text}</Alert>}
                  <Form onSubmit={handleProfileSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Roll Number</Label>
                          <Input
                            name="roll_number"
                            value={profileData.roll_number}
                            onChange={handleProfileChange}
                            disabled
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Name</Label>
                          <Input
                            name="name"
                            value={profileData.name}
                            onChange={handleProfileChange}
                            invalid={Boolean(formErrors?.name?.[0])}
                          />
                          <FormFeedback>{formErrors?.name?.[0] || ""}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Mobile</Label>
                          <Input
                            name="mobile"
                            value={profileData.mobile}
                            onChange={handleProfileChange}
                            invalid={Boolean(formErrors?.mobile?.[0])}
                          />
                          <FormFeedback>{formErrors?.mobile?.[0] || ""}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Email</Label>
                          <Input name="email" value={profileData.email} onChange={handleProfileChange} disabled readOnly />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>CNIC</Label>
                          <Input name="cnic" value={profileData.cnic} onChange={handleProfileChange} disabled readOnly />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Qualification</Label>
                          <Input
                            type="select"
                            name="qualification"
                            value={profileData.qualification}
                            onChange={handleProfileChange}
                            invalid={Boolean(formErrors?.qualification?.[0])}
                          >
                            <option value="">Select Qualification</option>
                            {educationOptions.qualifications.map(item => (
                              <option key={getOptionId(item)} value={getOptionId(item)}>
                                {getOptionLabel(item)}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formErrors?.qualification?.[0]}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Whatsapp Number</Label>
                          <Input
                            name="whatsapp"
                            value={profileData.whatsapp}
                            onChange={handleProfileChange}
                            invalid={Boolean(formErrors?.whatsapp?.[0])}
                          />
                          <FormFeedback>{formErrors?.whatsapp?.[0] || ""}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>City</Label>
                          <Input
                            type="select"
                            name="city"
                            value={profileData.city}
                            onChange={handleProfileChange}
                            invalid={Boolean(formErrors?.city?.[0])}
                          >
                            <option value="">Select City</option>
                            {educationOptions.cities.map(item => (
                              <option key={getOptionId(item)} value={getOptionId(item)}>
                                {getOptionLabel(item)}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formErrors?.city?.[0]}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label>Address</Label>
                      <Input
                        name="address"
                        value={profileData.address}
                        onChange={handleProfileChange}
                        invalid={Boolean(formErrors?.address?.[0])}
                      />
                      <FormFeedback>{formErrors?.address?.[0] || ""}</FormFeedback>
                    </FormGroup>

                    <Button className="w-100 mt-2 profile-action-btn" type="submit" disabled={profileLoading}>
                      {profileLoading ? "Updating..." : "Update"}
                    </Button>
                  </Form>
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            {activeTab === PASSWORD_TAB && (
              <Card>
                <CardBody>
                  <CardTitle className="h4 mb-4 text-uppercase text-center">Update Your Password</CardTitle>
                  {passwordAlert && <Alert color={passwordAlert.type}>{passwordAlert.text}</Alert>}
                  <Form onSubmit={handlePasswordSubmit}>
                    <FormGroup>
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        name="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        invalid={Boolean(passwordFormErrors?.current_password?.[0])}
                        required
                      />
                      <FormFeedback>{passwordFormErrors?.current_password?.[0] || ""}</FormFeedback>
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>New Password</Label>
                          <Input
                            type="password"
                            name="password"
                            value={passwordData.password}
                            onChange={handlePasswordChange}
                            invalid={Boolean(passwordFormErrors?.password?.[0])}
                            required
                          />
                          <FormFeedback>{passwordFormErrors?.password?.[0] || ""}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Confirm Password</Label>
                          <Input
                            type="password"
                            name="password_confirmation"
                            value={passwordData.password_confirmation}
                            onChange={handlePasswordChange}
                            invalid={Boolean(passwordFormErrors?.password_confirmation?.[0])}
                            required
                          />
                          <FormFeedback>{passwordFormErrors?.password_confirmation?.[0] || ""}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Button className="w-100 mt-2 profile-action-btn" type="submit" disabled={passwordLoading}>
                      {passwordLoading ? "Updating..." : "Update Password"}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default Profile;
